import React, { useEffect, useState, useRef, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp, addDoc, writeBatch, doc, serverTimestamp, deleteDoc, updateDoc } from 'firebase/firestore'; // Added updateDoc
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import * as XLSX from 'xlsx'; // For Excel parsing
// Ensure proper import of jsPDF and autoTable
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
// Simple CSV parser helper (handles basic quoting)
const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    return lines.map(line => {
        const fields = [];
        let currentField = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    currentField += '"';
                    i++; // Skip next quote
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                fields.push(currentField.trim()); // Trim whitespace around fields
                currentField = '';
            } else {
                currentField += char;
            }
        }
        fields.push(currentField.trim()); // Add last field
        return fields;
    });
};

// Basic CSS (can be moved to a separate file)
const styles = `
.laporan-logbook-container { padding: 20px; max-width: 1200px; margin: auto; font-family: sans-serif; }
.filter-section { margin-bottom: 20px; display: flex; flex-wrap: wrap; align-items: center; gap: 15px; }
.import-section { border: 1px solid #eee; padding: 15px; margin-top: 20px; border-radius: 5px; background-color: #f9f9f9; }
/* Basic Modal Styles */
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background-color: white; padding: 25px; border-radius: 8px; max-width: 600px; width: 90%; max-height: 80vh; overflow-y: auto; }
.modal-content h3 { margin-top: 0; }
.modal-content .form-group { margin-bottom: 15px; }
.modal-content label { display: block; margin-bottom: 5px; font-weight: bold; }
.modal-content input[type="text"], .modal-content input[type="date"], .modal-content input[type="time"], .modal-content textarea { width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.import-section h3 { margin-top: 0; }
.import-controls { display: flex; align-items: center; gap: 10px; margin-top: 10px; }
.logbook-table { width: 100%; border-collapse: collapse; margin-top: 20px; }
.logbook-table th, .logbook-table td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 14px; }
.logbook-table th { background-color: #f2f2f2; }
.error-message { color: red; margin-bottom: 15px; }
.success-message { color: green; margin-bottom: 15px; }
.loading-message { margin: 20px 0; text-align: center; }
.action-button { padding: 8px 15px; color: white; border: none; border-radius: 4px; cursor: pointer; }
.action-button:disabled { background-color: #ccc; cursor: not-allowed; }
.print-button { background-color: #007bff; }
.import-button { background-color: #28a745; }
select, input[type="file"] { padding: 8px; border-radius: 4px; border: 1px solid #ccc; }
select { min-width: 200px; }
`;

// Helper to format Firestore Timestamp or potential string dates
const formatTimestamp = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
  }
  // Handle potential string dates from import (basic check)
  if (typeof timestamp === 'string' && timestamp.includes(':')) { // Simple check for time presence
     try {
       // Attempt to create a date object and format it
       return new Date(timestamp).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
     } catch (e) {
       return timestamp; // Return original string if parsing fails
     }
  }
  if (typeof timestamp === 'string') return timestamp; // Return string if it doesn't look like a full timestamp
  return 'N/A';
};

// Helper to format Excel date serial number to YYYY-MM-DD
const formatExcelDate = (excelDate) => {
  if (typeof excelDate === 'number') {
    const date = XLSX.SSF.parse_date_code(excelDate);
    if (date) {
      // Pad month and day with leading zeros if necessary
      const year = date.y;
      const month = String(date.m).padStart(2, '0');
      const day = String(date.d).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
  // Try parsing string dates (basic formats)
  if (typeof excelDate === 'string') {
      try {
          const d = new Date(excelDate);
          if (!isNaN(d.getTime())) {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
          }
      } catch (e) { /* Ignore parsing errors */ }
  }
  return excelDate; // Return original if not a number or parsable string
};

// Helper to format Excel time serial number to HH:MM
const formatExcelTime = (excelTime) => {
    if (typeof excelTime === 'number') {
        // Excel stores time as a fraction of a day
        const totalSeconds = Math.round(excelTime * 86400);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        return `${hours}:${minutes}`;
    }
     // Try parsing string times (basic formats)
    if (typeof excelTime === 'string') {
        try {
            // Handle HH:MM or HH:MM:SS
            const parts = excelTime.split(':');
            if (parts.length >= 2) {
                const hours = String(parts[0]).padStart(2, '0');
                const minutes = String(parts[1]).padStart(2, '0');
                return `${hours}:${minutes}`;
            }
            // Handle potential full date-time strings from Excel
             const d = new Date(excelTime);
             if (!isNaN(d.getTime())) {
                 const hours = String(d.getHours()).padStart(2, '0');
                 const minutes = String(d.getMinutes()).padStart(2, '0');
                 return `${hours}:${minutes}`;
             }

        } catch (e) { /* Ignore parsing errors */ }
    }
    return excelTime; // Return original if not a number or parsable string
};


const LaporanLogbook = () => {
  const [logEntries, setLogEntries] = useState([]); // All fetched entries
  const [filteredLogEntries, setFilteredLogEntries] = useState([]); // Entries after filtering
  const [alatList, setAlatList] = useState([]);
  const [userList, setUserList] = useState([]); // Store users for filter dropdown
  const [selectedAlatId, setSelectedAlatId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(''); // Added user filter state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const fileInputRef = useRef(null);
  // State for editing
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    activity: '',
    activityDescription: '',
    conditionBefore: '',
    conditionAfter: '',
    tanggalMulai: '',
    jamMulai: '',
    tanggalSelesai: '',
    jamSelesai: '',
  });
  const [isUpdating, setIsUpdating] = useState(false); // Loading state for update
  const [dateFilterMode, setDateFilterMode] = useState('last3days'); // 'last3days', 'today', 'custom', 'all'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Fetch current admin user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Optionally verify admin role here if needed
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch Alat List and User List
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch Alat
        const instrumenRef = collection(db, 'instrumen');
        const alatGelasRef = collection(db, 'alat_gelas'); // Assuming glassware might have logs too? Adjust if only instrumen
        // Fetch all users for the filter dropdown, not just students
        const usersRef = collection(db, 'users');

        const [instrumenSnapshot, alatGelasSnapshot, usersSnapshot] = await Promise.all([
          getDocs(query(instrumenRef, orderBy('nama'))),
          getDocs(query(alatGelasRef, orderBy('nama'))),
          getDocs(query(usersRef, orderBy('nama'))) // Fetch all users, ordered by name
        ]);

        const instrumenData = instrumenSnapshot.docs.map(doc => ({ id: doc.id, nama: doc.data().nama, type: 'instrumen' }));
        const alatGelasData = alatGelasSnapshot.docs.map(doc => ({ id: doc.id, nama: doc.data().nama, type: 'alat_gelas' }));
        setAlatList([...instrumenData, ...alatGelasData].sort((a, b) => a.nama.localeCompare(b.nama))); // Ensure combined list is sorted

        const userData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUserList(userData); // Store all users

      } catch (err) {
        console.error("Error fetching initial data:", err);
        setError('Gagal memuat daftar alat atau pengguna.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch Logbook Entries with date filter for better performance
  const fetchAllLogbook = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const logbookRef = collection(db, 'logbook_instrumen');
      let querySnapshot;
      let dateFilter = '';
      
      // Determine date filter based on mode
      if (dateFilterMode === 'today') {
        // Today only
        const today = new Date();
        dateFilter = today.toISOString().split('T')[0]; // YYYY-MM-DD
      } else if (dateFilterMode === 'last3days') {
        // Last 3 days (default)
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        dateFilter = threeDaysAgo.toISOString().split('T')[0]; // YYYY-MM-DD
      } else if (dateFilterMode === 'custom') {
        // Custom date range
        if (!customStartDate || !customEndDate) {
          setError('Silakan pilih tanggal mulai dan tanggal akhir untuk filter custom.');
          setLoading(false);
          return;
        }
      } else if (dateFilterMode === 'all') {
        // Show all data
        dateFilter = null;
      }
      
      // Query with orderBy for sorting
      const q = query(
        logbookRef,
        orderBy('tanggalMulai', 'desc')
      );
      querySnapshot = await getDocs(q);
      
      // Process and filter data
      let data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      
      // Apply date filter
      if (dateFilterMode === 'today') {
        data = data.filter(log => log.tanggalMulai === dateFilter);
      } else if (dateFilterMode === 'last3days') {
        data = data.filter(log => {
          if (!log.tanggalMulai) return false;
          return log.tanggalMulai >= dateFilter;
        });
      } else if (dateFilterMode === 'custom') {
        data = data.filter(log => {
          if (!log.tanggalMulai) return false;
          return log.tanggalMulai >= customStartDate && log.tanggalMulai <= customEndDate;
        });
      }
      // If dateFilterMode === 'all', no date filtering
      
      // Sort by combined date and time (newest first)
      data.sort((a, b) => {
        const dateA = new Date(`${a.tanggalMulai}T${a.jamMulai || '00:00'}`);
        const dateB = new Date(`${b.tanggalMulai}T${b.jamMulai || '00:00'}`);
        const timeA = !isNaN(dateA.getTime()) ? dateA.getTime() : 0;
        const timeB = !isNaN(dateB.getTime()) ? dateB.getTime() : 0;
        return timeB - timeA; // Descending order
      });

      setLogEntries(data);

    } catch (err) {
      console.error("Error fetching logbook entries:", err);
      setError('Gagal memuat data logbook.');
      setLogEntries([]);
      setFilteredLogEntries([]); // Clear filtered list on error
    } finally {
      setLoading(false);
    }
  }, [dateFilterMode, customStartDate, customEndDate]);

  // Apply Filters Function (Client-side) - Only alat and user filters, date filter is handled in fetchAllLogbook
  const applyFilters = (entries) => {
    console.log("Applying filters. Initial entries:", entries.length); // Log initial count
    console.log("Filter values:", { selectedAlatId, selectedUserId }); // Log filter values
    let filtered = [...entries]; // Start with all entries

    // Filter by Alat
    if (selectedAlatId) {
      filtered = filtered.filter(log => log.instrumentId === selectedAlatId);
      console.log("After Alat filter:", filtered.length);
    }

    // Filter by User
    if (selectedUserId) {
      filtered = filtered.filter(log => log.userId === selectedUserId);
      console.log("After User filter:", filtered.length);
    }

    // Date filtering is now handled in fetchAllLogbook based on dateFilterMode
    // No need to filter by date here anymore

    console.log("Final filtered entries:", filtered.length);
    setFilteredLogEntries(filtered);
  };


  // Fetch Logbook Entries on initial load and when date filter changes
  useEffect(() => {
    fetchAllLogbook();
  }, [fetchAllLogbook]); // Re-fetch when fetchAllLogbook changes

  // Re-apply filters whenever filters or the base data change
  useEffect(() => {
    applyFilters(logEntries);
  }, [logEntries, selectedAlatId, selectedUserId]);

  // --- TEMPORARY: Disable client-side filtering for testing ---
  // useEffect(() => {
  //   console.log("TEMPORARY: Skipping client-side filters, showing all fetched entries.");
  //   setFilteredLogEntries(logEntries);
  // }, [logEntries]);
  // --- END TEMPORARY ---

  // Handle File Selection
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setImportError('');
    setImportSuccess('');
  };

  // Handle File Import (Excel or CSV)
  const handleImportFile = async () => {
    if (!selectedFile) {
      setImportError('Silakan pilih file Excel atau CSV terlebih dahulu.');
      return;
    }
    if (!currentUser) {
      setImportError('Admin tidak terautentikasi.');
      return;
    }

    setIsImporting(true);
    setImportError('');
    setImportSuccess('');

    const reader = new FileReader();

    // Determine how to read based on file type
    const isCsv = selectedFile.name.toLowerCase().endsWith('.csv');

    reader.onload = async (e) => {
      let json; // Will hold the array of arrays data
      try {
        const fileData = e.target.result;

        if (isCsv) {
          // Parse CSV text
          json = parseCSV(fileData); // Use the helper function
          if (json.length < 2) {
             throw new Error("File CSV kosong atau tidak memiliki header.");
          }
        } else {
          // Parse Excel file (existing logic)
          const workbook = XLSX.read(fileData, { type: 'binary', cellDates: false });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (json.length < 2) {
            throw new Error("File Excel kosong atau tidak memiliki header.");
          }
        }

        // --- Common Processing Logic Starts Here ---
        const headers = json[0].map(h => String(h || '').trim()); // Ensure headers are strings and trimmed
        const expectedHeaders = ["Nama Alat", "Nama Pengguna", "NIM", "Program Studi", "Kegiatan", "Kondisi Sebelum", "Kondisi Sesudah", "Mulai", "Selesai"];

        // Validate headers
        const missingHeaders = expectedHeaders.filter(eh => !headers.includes(eh));
        if (missingHeaders.length > 0) {
            throw new Error(`Header kolom file tidak sesuai. Kolom yang hilang: ${missingHeaders.join(', ')}`);
        }

        // Get indices of expected columns
        const colIndices = {};
        expectedHeaders.forEach(eh => {
            colIndices[eh] = headers.indexOf(eh);
        });

        const batch = writeBatch(db);
        const importedEntries = [];
        let rowNum = 1; // Start from data row 1 (index 1)

        for (rowNum = 1; rowNum < json.length; rowNum++) {
          const row = json[rowNum];
          // Ensure all row data is treated as string initially for consistent processing
          const namaAlat = String(row[colIndices["Nama Alat"]] || '').trim();
          const namaPengguna = String(row[colIndices["Nama Pengguna"]] || '').trim();
          const nim = String(row[colIndices["NIM"]] || '').trim();
          const programStudi = String(row[colIndices["Program Studi"]] || '').trim();
          const kegiatan = String(row[colIndices["Kegiatan"]] || '').trim();
          const kondisiSebelum = String(row[colIndices["Kondisi Sebelum"]] || '').trim();
          const kondisiSesudah = String(row[colIndices["Kondisi Sesudah"]] || '').trim();
          
          // Get time values from Mulai and Selesai columns
          const mulaiStr = String(row[colIndices["Mulai"]] || '').trim();
          const selesaiStr = String(row[colIndices["Selesai"]] || '').trim();
          
          // Parse date and time from Mulai and Selesai fields
          let tanggalMulai = '';
          let tanggalSelesai = '';
          let jamMulai = '';
          let jamSelesai = '';
          
          // Process Mulai
          if (mulaiStr && mulaiStr.includes('/')) {
            // Format might be DD/MM/YYYY HH:MM
            const parts = mulaiStr.split(' ');
            if (parts.length >= 1) {
              const dateParts = parts[0].split('/');
              if (dateParts.length === 3) {
                const day = dateParts[0].padStart(2, '0');
                const month = dateParts[1].padStart(2, '0');
                const year = dateParts[2];
                tanggalMulai = `${year}-${month}-${day}`;
              }
            }
            if (parts.length >= 2) {
              jamMulai = parts[1];
            }
          } else {
            // Just time format HH:MM
            jamMulai = formatExcelTime(mulaiStr);
            // Default date to today if not provided
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            tanggalMulai = `${year}-${month}-${day}`;
          }
          
          // Process Selesai (similar logic)
          if (selesaiStr && selesaiStr.includes('/')) {
            const parts = selesaiStr.split(' ');
            if (parts.length >= 1) {
              const dateParts = parts[0].split('/');
              if (dateParts.length === 3) {
                const day = dateParts[0].padStart(2, '0');
                const month = dateParts[1].padStart(2, '0');
                const year = dateParts[2];
                tanggalSelesai = `${year}-${month}-${day}`;
              }
            }
            if (parts.length >= 2) {
              jamSelesai = parts[1];
            }
          } else {
            // Just time format HH:MM
            jamSelesai = formatExcelTime(selesaiStr);
            tanggalSelesai = tanggalMulai; // Use same date as mulai
          }

          // Basic Validation
          if (!namaAlat || !nim || !kegiatan || !kondisiSebelum || !kondisiSesudah || !mulaiStr || !selesaiStr) {
            console.warn(`Baris ${rowNum + 1}: Melewati baris karena ada data yang kosong.`);
            continue; // Skip row if essential data is missing
          }
          
          // Validate date format
          if (!tanggalMulai || !jamMulai || !tanggalSelesai || !jamSelesai) {
            console.warn(`Baris ${rowNum + 1}: Format tanggal atau waktu tidak valid.`);
            continue; // Skip row if date/time format is invalid
          }

          // Find Instrument ID (but don't require it to exist)
          const instrument = alatList.find(a => a.nama.toLowerCase() === namaAlat.toLowerCase());
          
          // Find User by NIM (but don't require it to exist)
          const student = userList.find(u => u.nim === nim);
          
          const logEntryData = {
            instrumentId: instrument?.id || '',
            instrumentName: namaAlat,
            userId: student?.id || '', // Student's Firestore UID if found
            userName: namaPengguna || (student?.nama || student?.email || 'N/A'),
            userNim: nim,
            userProgramStudi: programStudi || (student?.programStudi || 'N/A'),
            activity: kegiatan,
            conditionBefore: kondisiSebelum,
            conditionAfter: kondisiSesudah,
            tanggalMulai: tanggalMulai,
            jamMulai: jamMulai,
            tanggalSelesai: tanggalSelesai,
            jamSelesai: jamSelesai,
            logTimestamp: serverTimestamp(), // Timestamp of import
            importedByAdmin: currentUser.uid, // Mark as imported by admin
          };

          const logDocRef = doc(collection(db, 'logbook_instrumen')); // Create new doc ref
          batch.set(logDocRef, logEntryData);
          importedEntries.push(logEntryData); // Keep track for success message
        }

        if (importedEntries.length === 0 && json.length > 1) {
             throw new Error("Tidak ada data valid yang dapat diimpor dari file.");
        } else if (importedEntries.length > 0) {
            await batch.commit();
            setImportSuccess(`Berhasil mengimpor ${importedEntries.length} entri logbook.`);
            fetchAllLogbook(); // Refresh the log list
        } else {
             setImportError("Tidak ada data valid untuk diimpor."); // Adjusted message
        }
        // --- End Common Processing Logic ---

      } catch (err) {
        console.error("Error importing file:", err);
        setImportError(`Gagal mengimpor: ${err.message}`);
      } finally {
        setIsImporting(false);
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFile(null);
      }
    }; // End of reader.onload

    reader.onerror = (err) => {
        console.error("File reading error:", err);
        setImportError("Gagal membaca file.");
        setIsImporting(false);
    };

    // Read file based on type
    if (isCsv) {
      reader.readAsText(selectedFile); // Read CSV as text
    } else {
      reader.readAsBinaryString(selectedFile); // Read Excel as binary string
    }
  }; // End of handleImportFile


  // Generate PDF Report (Updated with Program Studi)
  const handlePrintPDF = () => {
    try {
      // Create a new jsPDF document
      const doc = new jsPDF();
      const selectedAlat = alatList.find(a => a.id === selectedAlatId);
      const title = `Laporan Logbook ${selectedAlat ? `- ${selectedAlat.nama}` : '(Semua Alat)'}`;
      const timestamp = new Date().toLocaleString('id-ID');

      doc.setFontSize(16);
      doc.text(title, 14, 15);
      doc.setFontSize(10);
      doc.text(`Dicetak pada: ${timestamp}`, 14, 22);

      // Updated column headers for PDF
      const tableColumn = ["Nama Alat", "Nama Pengguna", "NIM", "Program Studi", "Kategori Kegiatan", "Deskripsi Kegiatan", "Kondisi Sebelum", "Kondisi Sesudah", "Mulai", "Selesai"];
      const tableRows = [];

      // Use filteredLogEntries for PDF export
      filteredLogEntries.forEach(log => {
        const logData = [
          log.instrumentName || 'N/A',
          log.userName || 'N/A',
          log.userNim || 'N/A',
          log.userProgramStudi || 'N/A',
          log.activity || '',
          log.activityDescription || '',
          log.conditionBefore || '',
          log.conditionAfter || '',
          `${log.tanggalMulai || ''} ${log.jamMulai || ''}`.trim(),
          `${log.tanggalSelesai || ''} ${log.jamSelesai || ''}`.trim(),
        ];
        tableRows.push(logData);
      });

      // Use the imported autoTable function directly
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
        theme: 'grid',
        headStyles: { fillColor: [22, 160, 133] },
        styles: { fontSize: 7 }, // Adjusted font size for more columns
        columnStyles: {
          0: { cellWidth: 20 }, // Nama Alat
          1: { cellWidth: 18 }, // Nama Pengguna
          2: { cellWidth: 12 }, // NIM
          3: { cellWidth: 18 }, // Program Studi
          4: { cellWidth: 15 }, // Kategori Kegiatan
          5: { cellWidth: 30 }, // Deskripsi Kegiatan
          6: { cellWidth: 15 }, // Kondisi Sebelum
          7: { cellWidth: 15 }, // Kondisi Sesudah
          8: { cellWidth: 15 }, // Mulai
          9: { cellWidth: 15 }, // Selesai
        }
      });

      const safeAlatName = selectedAlat ? selectedAlat.nama.replace(/[^a-z0-9]/gi, '_') : 'SemuaAlat';
      doc.save(`Laporan_Logbook_${safeAlatName}_${Date.now()}.pdf`);
      
    } catch (err) {
      console.error("Error generating PDF:", err);
      setError(`Gagal membuat PDF: ${err.message}`);
    }
  };

  // --- Export to CSV Function ---
  const handleExportCSV = () => {
    if (filteredLogEntries.length === 0) {
      alert('Tidak ada data untuk diekspor.');
      return;
    }

    try {
      const headers = ["Nama Alat", "Nama Pengguna", "NIM", "Program Studi", "Kategori Kegiatan", "Deskripsi Kegiatan", "Kondisi Sebelum", "Kondisi Sesudah", "Tanggal Mulai", "Jam Mulai", "Tanggal Selesai", "Jam Selesai"];
      const rows = filteredLogEntries.map(log => [
        log.instrumentName || '',
        log.userName || '',
        log.userNim || '',
        log.userProgramStudi || '',
        log.activity || '',
        log.activityDescription || '',
        log.conditionBefore || '',
        log.conditionAfter || '',
        log.tanggalMulai || '',
        log.jamMulai || '',
        log.tanggalSelesai || '',
        log.jamSelesai || '',
      ]);

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Laporan Logbook");

      // Generate filename
      const selectedAlat = alatList.find(a => a.id === selectedAlatId);
      const safeAlatName = selectedAlat ? selectedAlat.nama.replace(/[^a-z0-9]/gi, '_') : 'SemuaAlat';
      const fileName = `Laporan_Logbook_${safeAlatName}_${Date.now()}.xlsx`; // Use .xlsx for broader compatibility

      // Trigger download
      XLSX.writeFile(wb, fileName);

    } catch (err) {
      console.error("Error generating CSV:", err);
      setError(`Gagal membuat file Excel: ${err.message}`);
    }
  };


  // --- Edit Log Functions ---
  const handleEditLog = (log) => { // Pass the whole log object
    if (!log) return;
    setEditingLogId(log.id);
    setEditFormData({
      activity: log.activity || '',
      activityDescription: log.activityDescription || '',
      conditionBefore: log.conditionBefore || '',
      conditionAfter: log.conditionAfter || '',
      tanggalMulai: log.tanggalMulai || '',
      jamMulai: log.jamMulai || '',
      tanggalSelesai: log.tanggalSelesai || '',
      jamSelesai: log.jamSelesai || '',
    });
    setIsEditingModalOpen(true);
      setError(''); // Clear main error
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateLog = async (e) => {
    e.preventDefault(); // Prevent default form submission
    if (!editingLogId) return;

    // Basic validation for edit form
    if (!editFormData.activity || !editFormData.activityDescription || !editFormData.conditionBefore || !editFormData.conditionAfter ||
        !editFormData.tanggalMulai || !editFormData.jamMulai || !editFormData.tanggalSelesai || !editFormData.jamSelesai) {
      // Display error within the modal? For now, alert.
      alert('Semua field dalam form edit wajib diisi.');
      return;
    }
     // Combine date/time and validate sequence
    const startDateTime = new Date(`${editFormData.tanggalMulai}T${editFormData.jamMulai}`);
    const endDateTime = new Date(`${editFormData.tanggalSelesai}T${editFormData.jamSelesai}`);
     if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        alert('Format tanggal atau waktu tidak valid.');
        return;
    }
    if (endDateTime <= startDateTime) {
        alert('Waktu selesai harus setelah waktu mulai.');
        return;
    }
    // Note: Skipping loan period validation for admin edits for simplicity

    setIsUpdating(true);
    setError('');
    try {
      const logDocRef = doc(db, 'logbook_instrumen', editingLogId);
      await updateDoc(logDocRef, {
        activity: editFormData.activity,
        activityDescription: editFormData.activityDescription,
        conditionBefore: editFormData.conditionBefore,
        conditionAfter: editFormData.conditionAfter,
        tanggalMulai: editFormData.tanggalMulai,
        jamMulai: editFormData.jamMulai,
        tanggalSelesai: editFormData.tanggalSelesai,
        jamSelesai: editFormData.jamSelesai,
        // Optionally add an 'lastEditedBy' field: lastEditedBy: currentUser.uid,
        // Optionally add an 'lastEditedAt' field: lastEditedAt: serverTimestamp()
      });
      closeEditModal(); // Close modal on success
      fetchAllLogbook(); // Refresh the full list
      // Optionally show success message
      setImportSuccess('Entri logbook berhasil diperbarui.');

    } catch (err) {
      console.error("Error updating log entry:", err);
      // Display error within the modal? For now, alert.
      alert(`Gagal memperbarui entri: ${err.message}`);
      setError(`Gagal memperbarui entri: ${err.message}`); // Also set main error state
    } finally {
      setIsUpdating(false);
    }
  };

  const closeEditModal = () => {
    setIsEditingModalOpen(false);
    setEditingLogId(null);
    setEditFormData({ // Reset form data
      activity: '', activityDescription: '', conditionBefore: '', conditionAfter: '',
      tanggalMulai: '', jamMulai: '', tanggalSelesai: '', jamSelesai: '',
    });
  };

  // --- Delete Log Function ---
  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus entri logbook ini?')) {
      return;
    }
    setError(''); // Clear previous errors
    try {
      setLoading(true); // Indicate loading during delete
      const logDocRef = doc(db, 'logbook_instrumen', logId);
      await deleteDoc(logDocRef);
      // Refresh the log list after deletion
      fetchAllLogbook(); // Refresh the full list
      // Optionally show a success message (could use state like importSuccess)
      // setImportSuccess('Entri logbook berhasil dihapus.');
    } catch (err) {
      console.error("Error deleting log entry:", err);
      setError(`Gagal menghapus entri logbook: ${err.message}`);
    } finally {
      setLoading(false); // Stop loading indicator
    }
  };


  return (
    <div className="laporan-logbook-container">
      <style>{styles}</style>
      <h2>Laporan Logbook Instrumen</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Filter and Export Section */}
      <div className="filter-section">
        {/* Date Filter Mode Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold', marginRight: '5px' }}>Periode:</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input
              type="radio"
              name="dateFilterMode"
              value="last3days"
              checked={dateFilterMode === 'last3days'}
              onChange={(e) => setDateFilterMode(e.target.value)}
              disabled={loading || isImporting}
            />
            <span>3 Hari Terakhir (Default)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input
              type="radio"
              name="dateFilterMode"
              value="today"
              checked={dateFilterMode === 'today'}
              onChange={(e) => setDateFilterMode(e.target.value)}
              disabled={loading || isImporting}
            />
            <span>Hari Ini</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input
              type="radio"
              name="dateFilterMode"
              value="custom"
              checked={dateFilterMode === 'custom'}
              onChange={(e) => setDateFilterMode(e.target.value)}
              disabled={loading || isImporting}
            />
            <span>Tanggal</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input
              type="radio"
              name="dateFilterMode"
              value="all"
              checked={dateFilterMode === 'all'}
              onChange={(e) => setDateFilterMode(e.target.value)}
              disabled={loading || isImporting}
            />
            <span>Semua Data</span>
          </label>
        </div>
        
        {/* Custom Date Range Inputs */}
        {dateFilterMode === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
            <label htmlFor="customStartDate" style={{ marginRight: '5px' }}>Dari:</label>
            <input
              type="date"
              id="customStartDate"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              disabled={loading || isImporting}
              style={{ padding: '5px' }}
            />
            <label htmlFor="customEndDate" style={{ marginLeft: '10px', marginRight: '5px' }}>Sampai:</label>
            <input
              type="date"
              id="customEndDate"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              min={customStartDate}
              disabled={loading || isImporting}
              style={{ padding: '5px' }}
            />
          </div>
        )}
        
        {/* Alat Filter */}
        <div>
          <label htmlFor="alatFilter" style={{ marginRight: '5px' }}>Alat:</label>
          <select
            id="alatFilter"
            value={selectedAlatId}
            onChange={(e) => setSelectedAlatId(e.target.value)}
            disabled={loading || isImporting}
          >
            <option value="">-- Semua Alat --</option>
            {alatList.map(alat => (
              <option key={alat.id} value={alat.id}>
                {alat.nama} ({alat.type === 'instrumen' ? 'Instrumen' : 'Alat Gelas'})
              </option>
            ))}
          </select>
        </div>
        {/* User Filter */}
        <div>
          <label htmlFor="userFilter" style={{ marginRight: '5px' }}>Pengguna:</label>
          <select
            id="userFilter"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            disabled={loading || isImporting}
          >
            <option value="">-- Semua Pengguna --</option>
            {userList.map(user => (
              <option key={user.id} value={user.id}>
                {user.nama || user.email} ({user.nim || user.nip || user.role})
              </option>
            ))}
          </select>
        </div>
        {/* Export Buttons */}
        <button
          onClick={handlePrintPDF}
          className="action-button print-button"
          disabled={loading || isImporting || filteredLogEntries.length === 0}
          style={{ backgroundColor: '#007bff' }} // Explicit style
        >
          Cetak PDF
        </button>
        <button
          onClick={handleExportCSV}
          className="action-button" // Generic class, specific style below
          disabled={loading || isImporting || filteredLogEntries.length === 0}
          style={{ backgroundColor: '#17a2b8' }} // Teal color for CSV/Excel
        >
          Export Excel
        </button>
      </div>

      {/* Import Section */}
      <div className="import-section">
        <h3>Impor Logbook dari File</h3> {/* Updated Title */}
        {importError && <div className="error-message">{importError}</div>}
        {importSuccess && <div className="success-message">{importSuccess}</div>}
        <div className="import-controls">
          <input
            type="file"
            accept=".xlsx, .xls, .csv" // Added .csv
            onChange={handleFileChange}
            ref={fileInputRef}
            disabled={isImporting}
          />
          <button
            onClick={handleImportFile}
            className="action-button import-button"
            disabled={!selectedFile || isImporting || loading || !currentUser}
          >
            {isImporting ? 'Mengimpor...' : 'Impor File'} {/* Updated Button Text */}
          </button>
        </div>
         <small>Format Kolom (Excel/CSV): Nama Alat	Nama Pengguna	NIM	Program Studi	Kegiatan	Kondisi Sebelum	Kondisi Sesudah	Mulai	Selesai</small> {/* Updated Help Text */}
      </div>


      {/* Log Table Section */}
      {loading && !isImporting ? ( // Show loading only if not importing
        <div className="loading-message">Memuat data logbook...</div>
      ) : (
        <div className="logbook-table-container">
          <table className="logbook-table">
            <thead>
              <tr>
                <th>Nama Alat</th>
                <th>Nama Pengguna</th>
                <th>NIM</th>
                <th>Program Studi</th>
                <th>Kegiatan</th>
                <th>Kondisi Sebelum</th>
                <th>Kondisi Sesudah</th>
                <th>Mulai</th>
                <th>Selesai</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {/* Use filteredLogEntries for display */}
              {filteredLogEntries.length > 0 ? (
                filteredLogEntries.map((log) => (
                  <tr key={log.id}>
                  <td>{log.instrumentName || 'N/A'}</td>
                  <td>{log.userName || 'N/A'}</td>
                  <td>{log.userNim || 'N/A'}</td>
                  <td>{log.userProgramStudi || 'N/A'}</td>
                  <td>
                    <div><strong>{log.activity || ''}</strong></div>
                    {log.activityDescription && (
                      <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                        {log.activityDescription}
                      </div>
                    )}
                  </td>
                  <td>{log.conditionBefore || ''}</td>
                  <td>{log.conditionAfter || ''}</td>
                  <td>{`${log.tanggalMulai || ''} ${log.jamMulai || ''}`.trim()}</td>
                  <td>{`${log.tanggalSelesai || ''} ${log.jamSelesai || ''}`.trim()}</td>
                  {/* Removed Kembali Aktual and Diimpor? cells */}
                  <td className="action-buttons"> {/* Added Actions Cell */}
                    <button
                      onClick={() => handleEditLog(log)} // Pass the whole log object - Kept the correct one
                      className="btn btn-edit"
                      style={{ marginRight: '5px', padding: '3px 8px', fontSize: '12px' }}
                      disabled={isImporting || loading || isUpdating} // Disable during actions
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteLog(log.id)}
                      className="btn btn-delete"
                      style={{ padding: '3px 8px', fontSize: '12px' }}
                      disabled={isImporting || loading || isUpdating} // Disable during actions
                    >
                      Hapus
                    </button>
                    </td>
                  </tr>
                ))
              ) : (
                // Show message based on filters and loading state
                (loading && !isImporting) ? (
                   <tr><td colSpan="10" style={{ textAlign: 'center' }}>Memuat...</td></tr>
                ) : (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center' }}>
                      Tidak ada data logbook yang cocok dengan filter yang dipilih.
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Log Modal */}
      {isEditingModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Entri Logbook</h3>
            <form onSubmit={handleUpdateLog}>
              {/* Display non-editable info */}
              <p><strong>Nama Alat:</strong> {logEntries.find(l => l.id === editingLogId)?.instrumentName || 'N/A'}</p>
              <p><strong>Pengguna:</strong> {logEntries.find(l => l.id === editingLogId)?.userName || 'N/A'} ({logEntries.find(l => l.id === editingLogId)?.userNim || 'N/A'})</p>
              <hr style={{ margin: '15px 0' }}/>

              {/* Editable fields */}
              <div className="form-group">
                <label htmlFor="editActivity">Kategori Kegiatan:</label>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="activity"
                      value="Praktikum"
                      checked={editFormData.activity === 'Praktikum'}
                      onChange={handleEditFormChange}
                      disabled={isUpdating}
                    />
                    <span>Praktikum</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="activity"
                      value="Penelitian"
                      checked={editFormData.activity === 'Penelitian'}
                      onChange={handleEditFormChange}
                      disabled={isUpdating}
                    />
                    <span>Penelitian</span>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="editActivityDescription">Deskripsi Kegiatan: <span style={{ fontSize: '0.85rem', color: '#666' }}>(Gunakan kalimat aktif)</span></label>
                <input
                  type="text"
                  id="editActivityDescription"
                  name="activityDescription"
                  value={editFormData.activityDescription}
                  onChange={handleEditFormChange}
                  placeholder="Contoh: Menimbang Niacinamide, Carbopol, dan Methyl Paraben"
                  required
                  disabled={isUpdating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="editConditionBefore">Kondisi Sebelum:</label>
                <input type="text" id="editConditionBefore" name="conditionBefore" value={editFormData.conditionBefore} onChange={handleEditFormChange} required disabled={isUpdating} />
              </div>
              <div className="form-group">
                <label htmlFor="editConditionAfter">Kondisi Sesudah:</label>
                <input type="text" id="editConditionAfter" name="conditionAfter" value={editFormData.conditionAfter} onChange={handleEditFormChange} required disabled={isUpdating} />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="editTanggalMulai">Tanggal Mulai:</label>
                  <input type="date" id="editTanggalMulai" name="tanggalMulai" value={editFormData.tanggalMulai} onChange={handleEditFormChange} required disabled={isUpdating} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="editJamMulai">Jam Mulai:</label>
                  <input type="time" id="editJamMulai" name="jamMulai" value={editFormData.jamMulai} onChange={handleEditFormChange} required disabled={isUpdating} />
                </div>
              </div>
               <div style={{ display: 'flex', gap: '10px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="editTanggalSelesai">Tanggal Selesai:</label>
                  <input type="date" id="editTanggalSelesai" name="tanggalSelesai" value={editFormData.tanggalSelesai} onChange={handleEditFormChange} required disabled={isUpdating} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="editJamSelesai">Jam Selesai:</label>
                  <input type="time" id="editJamSelesai" name="jamSelesai" value={editFormData.jamSelesai} onChange={handleEditFormChange} required disabled={isUpdating} />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeEditModal} disabled={isUpdating} className="btn btn-secondary">Batal</button>
                <button type="submit" disabled={isUpdating} className="btn btn-primary">
                  {isUpdating ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaporanLogbook;
